// Contexto de sesión. Un solo lugar donde vive "quién está usando la app".
//
// Al montar, si hay un token guardado, pregunta al backend quién es.
// Eso valida el token de paso: si venció y no se puede renovar, el
// usuario vuelve al login sin ver una pantalla rota.

import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { invalidarOpciones } from "./opciones";
import * as api from "./api";
import { idiomaActual, ponerIdioma } from "./i18n";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [usuario, setUsuario] = useState(null);
  const [perfil, setPerfil] = useState(null);

  /* EL IDIOMA VIVE EN EL PERFIL Y TIENE QUE LLEGAR AL MÓDULO QUE TRADUCE.
   *
   * ⚠️  Sin esta línea `perfiles.idioma` seguiría siendo lo que era hasta
   *     hoy: una columna que se guarda, un selector que se puede tocar, y
   *     cero efecto. El propio código lo admitía en `lib/rutas_perfil.js`
   *     — "idioma → la interfaz (parcial: falta traducir)".
   *
   * Va envuelto en un setter propio en vez de en un `useEffect` sobre
   * `perfil`: así el idioma cambia en el MISMO render en que llega el
   * perfil, y no un tick después con la pantalla ya pintada en español. */
  const ponerPerfil = useCallback((p) => {
    /* ⚠️  EL RESPALDO ES `idiomaActual()` Y NO "es" (2026-09-05).
     *
     * Decía `|| "es"`. Con el idioma del visitante en inglés y con
     * memoria, ese respaldo tiraba a alguien de vuelta al español justo
     * en el momento de entrar: elegía francés en la pantalla de login,
     * se registraba, y la app le contestaba en español. El idioma que
     * ya está puesto es una respuesta mejor que una constante. */
    ponerIdioma(p?.idioma || idiomaActual());
    setPerfil(p);
  }, []);
  // La estación de hoy en el país del usuario. La calcula el servidor
  // en /auth/me: null cuando no se puede saber, y ahí la pantalla no
  // preselecciona nada en vez de adivinar.
  const [estacionActual, setEstacionActual] = useState(null);

  /* El plan, tal como lo manda `/auth/me`. Vive acá y no en la pantalla
   * del perfil porque lo leen DOS lugares —el bloque del perfil y el
   * aviso de arriba— y pedirlo dos veces sería pedir dos veces lo
   * mismo. Ver `planParaLaPantalla` en server.js. */
  const [plan, setPlan] = useState(null);
  // "cargando" arranca en true a propósito: hasta no saber si hay sesión
  // válida no se puede decidir qué pantalla mostrar. Sin esto, el login
  // parpadea un instante antes de entrar.
  const [cargando, setCargando] = useState(true);

  const cargarUsuario = useCallback(async () => {
    if (!api.leerSesion()) {
      setUsuario(null);
      ponerPerfil(null);
      setCargando(false);
      return;
    }

    try {
      const data = await api.quienSoy();
      setUsuario(data.usuario);
      ponerPerfil(data.perfil);
      setEstacionActual(data.estacionActual || null);
      setPlan(data.plan || null);
    } catch {
      api.borrarSesion();
      setUsuario(null);
      ponerPerfil(null);
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    cargarUsuario();
  }, [cargarUsuario]);

  /* ESCUCHAR QUE LA SESIÓN SE MURIÓ (2026-08-24, CR-8).
   *
   * `api.js` borra la sesión cuando el refresh falla, pero hasta hoy no
   * se lo decía a nadie: `usuario` seguía seteado y la app entera quedaba
   * montada, con todos los botones respondiendo el mismo error y sin
   * ningún camino de vuelta al login. La única salida era recargar la
   * página a mano.
   *
   * Con esto, la app vuelve al login sola. No hay nada que preservar del
   * otro lado: sin token no se puede hacer absolutamente nada.
   *
   * El `useEffect` devuelve la desuscripción — un contexto que se
   * desmonta y deja su oyente vivo es una fuga que después setea estado
   * de un componente que ya no existe.
   */
  useEffect(() => {
    return api.alMorirLaSesion(() => {
      setUsuario(null);
      ponerPerfil(null);
      setEstacionActual(null);
      setCargando(false);
      // Las listas propias son de ESE usuario. Sin esto, quien entre
      // después en la misma pestaña vería las ocasiones del anterior.
      invalidarOpciones();
    });
  }, []);

  const entrar = async (email, password) => {
    const data = await api.entrar(email, password);
    setUsuario(data.usuario);
    // El perfil llega en /me, que trae también el idioma y perfil_estilo
    const yo = await api.quienSoy();
    ponerPerfil(yo.perfil);
    setEstacionActual(yo.estacionActual || null);
    return data;
  };

  /* ⚠️  EL CUARTO PARÁMETRO FALTABA, Y SE CAÍA AL PISO SIN UNA QUEJA.
   *     (2026-09-11 — todo el que se registró desde el 5/9 quedó con el
   *     idioma equivocado.)
   *
   * `Login.js` llamaba bien:
   *
   *     registrarse(email, password, nombre, idiomaActual())
   *
   * pero este envoltorio declaraba TRES parámetros y reenviaba tres.
   * JavaScript no se queja de un argumento de más: simplemente no
   * llega. `api.registrarse` recibía `idioma === undefined`,
   * `JSON.stringify` borra las claves con `undefined`, y el servidor
   * veía un alta sin idioma. El trigger caía en su default —`es`— y
   * `AuthContext` le aplicaba ESE idioma a la persona apenas entraba.
   *
   * Por eso alguien elegía inglés en la portada y la bienvenida le
   * salía en español: el idioma sí viajaba desde la pantalla, y moría
   * acá, en la única línea que no lo nombraba.
   *
   * ⚠️  NINGÚN GUARDIÁN PODÍA VERLO, y conviene entender por qué:
   *     `no-undef` no aplica (el nombre existe), `react-hooks` tampoco,
   *     y `test_api_cliente.js` mira `api.js` —que estaba BIEN—, no
   *     quién lo llama. El hueco estaba entre dos archivos correctos.
   *     `test_alta.js` lo cierra: compara la aridad de cada envoltorio
   *     con la de la función de `api` que reenvía, y además EJECUTA el
   *     alta para ver qué llega del otro lado. */
  const registrarse = async (email, password, nombre, idioma) => {
    const data = await api.registrarse(email, password, nombre, idioma);

    // Con confirmación de email activada no hay sesión todavía:
    // el usuario tiene que ir a su correo antes de poder entrar.
    if (data.sesion) {
      setUsuario(data.usuario);
      const yo = await api.quienSoy();
      ponerPerfil(yo.perfil);
      setEstacionActual(yo.estacionActual || null);
    }

    return data;
  };

  const salir = async () => {
    await api.salir();
    // El caché de opciones tiene los colores y ocasiones DE ESTE
    // usuario. Sin tirarlo, si alguien entra con otra cuenta en la
    // misma pestaña vería las listas propias del anterior — y las
    // ocasiones de otro no son un detalle cosmético: alimentan la regla
    // de formalidad.
    invalidarOpciones();
    setUsuario(null);
    ponerPerfil(null);
  };

  const actualizarPerfil = async (cambios) => {
    const data = await api.actualizarPerfil(cambios);
    ponerPerfil(data.perfil);
    return data.perfil;
  };

  return (
    <AuthContext.Provider
      value={{
        usuario,
        perfil,
        estacionActual,
        plan,
        cargando,
        entrar,
        registrarse,
        salir,
        actualizarPerfil,
        recargar: cargarUsuario,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth tiene que usarse adentro de <AuthProvider>");
  }
  return ctx;
}
